import { useEffect, useRef } from "react";
import { getLoadRequestDetailsByID } from "apis/myRequestsService";

interface EnrichableNotification {
  id: string;
  type?: string;
  requestDetailId?: number | null;
  message: string;
  flightNumber?: string;
}

// "request_answered" notifications don't carry a flight number from the
// backend at all. Fetch it client-side, via the same endpoint the
// notification-click routing already uses, so the UI can show it alongside
// the notification (kept out of the message text itself — the message is
// rendered next to a Flight No/Date/Time line, so baking it into the message
// too would just duplicate it).
export function useEnrichRequestAnsweredNotifications<
  T extends EnrichableNotification,
>(
  notifications: T[],
  setNotifications: React.Dispatch<React.SetStateAction<T[]>>,
) {
  const enrichedIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const pending = notifications.filter(
      (n) =>
        n.type === "request_answered" &&
        n.requestDetailId != null &&
        !n.flightNumber &&
        !enrichedIdsRef.current.has(n.id),
    );
    if (pending.length === 0) return;

    pending.forEach((n) => {
      enrichedIdsRef.current.add(n.id);
      (async () => {
        try {
          const data: any = await getLoadRequestDetailsByID(
            Number(n.requestDetailId),
          );
          const flightNumber = data?.flightNo || data?.flightNumber || "";
          if (!flightNumber) return;

          setNotifications((prev) =>
            prev.map((item) =>
              item.id === n.id ? { ...item, flightNumber } : item,
            ),
          );
        } catch (error) {
          console.error(
            `Failed to enrich notification ${n.id} with flight info:`,
            error,
          );
        }
      })();
    });
  }, [notifications, setNotifications]);
}
